/**
 * INNTIL — kryssalg i handlekurv-skuffa.
 *
 * Henter seksjonen `inntil-cart-cross-sell` gjennom Shopifys Section
 * Rendering API og legger den inn i skuffa. Det er den eneste veien til
 * `recommendations`-objektet, som er der Search & Discovery sine
 * komplementære produkter kommer ut.
 *
 * HVORFOR ET CUSTOM ELEMENT OG IKKE EN LYTTER PÅ KURVHENDELSER. Horizon
 * re-rendrer hele skuffseksjonen serverside og morfer den inn ved hver
 * endring. Da byttes `data-product-id` av seg selv, og
 * `attributeChangedCallback` fyrer. Vi slipper å vite hva Horizons
 * hendelser heter, og slipper å holde oss oppdatert når de endrer dem.
 *
 * Bygget 23.09.2026.
 */
class InntilCartCrossSell extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-id'];
  }

  connectedCallback() {
    this.hent();

    /* HVORFOR EN OBSERVER OG IKKE BARE connectedCallback.
       Horizon morfer skuffseksjonen inn ved hver kurvendring. Morfingen
       gjenbruker dette elementet og bytter bare barna — mot serverens
       versjon, som er tom. Da fyrer verken connectedCallback eller
       attributeChangedCallback, og raden blir stående tom.
       Målt 23.09.2026: etter et klikk på «Legg til» var blokka borte.

       Vi ser derfor på våre egne barn. Blir de fjernet av noen andre enn
       oss, henter vi på nytt. */
    this.vakt = new MutationObserver(() => {
      if (this.skriver) return;
      if (this.childElementCount === 0 && this.dataset.productId) {
        this.hentetFor = null;
        this.hent();
      }
    });
    this.vakt.observe(this, { childList: true });
  }

  attributeChangedCallback(navn, gammel, ny) {
    if (gammel !== null && gammel !== ny) this.hent();
  }

  disconnectedCallback() {
    /* Et svar som kommer etter at skuffa er lukket eller morfet bort skal
       ikke skrives inn i et element som ikke lenger står i dokumentet. */
    if (this.avbryt) this.avbryt.abort();
    if (this.vakt) this.vakt.disconnect();
  }

  async hent() {
    const id = this.dataset.productId;
    if (!id) return;

    /* Samme id to ganger på rad betyr at skuffa ble re-rendret uten at
       den siste varen endret seg. Da står innholdet allerede riktig. */
    if (this.hentetFor === id && this.childElementCount > 0) return;

    if (this.avbryt) this.avbryt.abort();
    this.avbryt = new AbortController();

    const url =
      `${this.dataset.url}?section_id=${encodeURIComponent(this.dataset.section)}` +
      `&product_id=${encodeURIComponent(id)}` +
      `&intent=${encodeURIComponent(this.dataset.intent || 'complementary')}` +
      `&limit=${encodeURIComponent(this.dataset.limit || '6')}`;

    try {
      const svar = await fetch(url, { signal: this.avbryt.signal });
      if (!svar.ok) return;
      const tekst = await svar.text();

      /* Svaret er hele seksjonen med Shopifys egen wrapper rundt. Vi vil
         bare ha innmaten, ellers får vi en `shopify-section`-div inne i
         skuffa som ikke hører hjemme der. */
      const doc = new DOMParser().parseFromString(tekst, 'text/html');
      const innhold = doc.querySelector('.inntil-cx');

      /* Våre egne skrivinger skal ikke vekke observeren. */
      this.skriver = true;
      this.hentetFor = id;
      this.replaceChildren();
      if (innhold) this.appendChild(innhold);
      this.skriver = false;
    } catch (e) {
      /* Avbrutt kall er normalt, ikke en feil. Alt annet lar vi ligge —
         en tom rad er et akseptabelt utfall, en krasj i skuffa er det ikke. */
      if (e.name !== 'AbortError') this.hentetFor = null;
    }
  }
}

if (!customElements.get('inntil-cart-cross-sell')) {
  customElements.define('inntil-cart-cross-sell', InntilCartCrossSell);
}
